(() => {
  const KEY = 'beautyoutlet:wishlist';
  const MAX_HANDLES = 50;

  // Shared product cache (also used by recently-viewed.js). Module-level
  // so the drawer doesn't refetch handles within the same session.
  if (!window.__BO_PRODUCT_CACHE__) window.__BO_PRODUCT_CACHE__ = new Map();
  const productCache = window.__BO_PRODUCT_CACHE__;

  // Simple semaphore: cap concurrent product requests at 4. They go to
  // templates/product.data.liquid rather than /products/{handle}.js so a
  // product above the viewer's access tier comes back null and is skipped.
  const fetchProduct = (() => {
    const MAX = 4;
    const queue = [];
    let active = 0;
    const drain = () => {
      while (active < MAX && queue.length) {
        const { handle, resolve } = queue.shift();
        active++;
        if (productCache.has(handle)) {
          active--;
          resolve(productCache.get(handle));
          drain();
          continue;
        }
        fetch(`/products/${encodeURIComponent(handle)}?view=data`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((p) => {
            if (p) productCache.set(handle, p);
            resolve(p);
            active--;
            drain();
          });
      }
    };
    return (handle) =>
      new Promise((resolve) => {
        queue.push({ handle, resolve });
        drain();
      });
  })();

  // Storage API
  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  };
  const write = (handles) => {
    // Cap at MAX_HANDLES — drop oldest (first entries).
    const capped = handles.length > MAX_HANDLES ? handles.slice(handles.length - MAX_HANDLES) : handles;
    localStorage.setItem(KEY, JSON.stringify(capped));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: capped } }));
  };
  const has = (handle) => read().includes(handle);
  const add = (handle) => {
    const list = read();
    if (!list.includes(handle)) write([...list, handle]);
  };
  const remove = (handle) => write(read().filter((h) => h !== handle));
  const toggle = (handle) => (has(handle) ? remove(handle) : add(handle));
  const clear = () => write([]);

  // Format money — uses Shopify default money format (€) when available
  const formatMoney = (cents) => {
    const value = (Number(cents) / 100).toFixed(2);
    return `${value.replace('.', ',')} €`;
  };

  // ── Drawer + focus trap (fallback if drawer-focus.js not loaded) ──
  const drawer = document.querySelector('[data-wishlist-drawer]');

  const FOCUSABLE_SELECTOR =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const focusState = { last: null, keyHandler: null };

  const getFocusable = () =>
    Array.from(drawer?.querySelectorAll(FOCUSABLE_SELECTOR) || []).filter((el) => {
      if (el.hidden || el.closest('[hidden]') || el.closest('[inert]')) return false;
      const s = window.getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden';
    });

  const setBackgroundInert = (on) => {
    if (!drawer) return;
    Array.from(document.body.children).forEach((node) => {
      if (node === drawer || node.contains(drawer)) return;
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      if (on) {
        if (!node.hasAttribute('inert')) {
          node.setAttribute('inert', '');
          node.dataset.wlInertApplied = '1';
        }
      } else if (node.dataset.wlInertApplied === '1') {
        node.removeAttribute('inert');
        delete node.dataset.wlInertApplied;
      }
    });
  };

  const trapKeydown = (e) => {
    if (e.key !== 'Tab') return;
    const f = getFocusable();
    if (f.length === 0) {
      e.preventDefault();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey ? document.activeElement === first || !drawer.contains(document.activeElement) : document.activeElement === last) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  };

  const activateFocus = () => {
    if (!drawer || window.DrawerFocus) return;
    focusState.last = document.activeElement;
    setBackgroundInert(true);
    focusState.keyHandler = trapKeydown;
    drawer.addEventListener('keydown', focusState.keyHandler);
    requestAnimationFrame(() => {
      const closeBtn = drawer.querySelector('[data-wishlist-close]:not(.wishlist-drawer__overlay)');
      const target = closeBtn || getFocusable()[0];
      target?.focus({ preventScroll: false });
    });
  };
  const deactivateFocus = () => {
    if (!drawer || window.DrawerFocus) return;
    setBackgroundInert(false);
    if (focusState.keyHandler) {
      drawer.removeEventListener('keydown', focusState.keyHandler);
      focusState.keyHandler = null;
    }
    if (focusState.last && document.contains(focusState.last)) {
      try { focusState.last.focus(); } catch { /* noop */ }
    }
    focusState.last = null;
  };

  const open = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wishlist-drawer-open');
    hydrate();
    activateFocus();
  };
  const close = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wishlist-drawer-open');
    deactivateFocus();
  };

  // Sync product card heart toggles
  const syncToggles = () => {
    const list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.handle;
      if (!handle) return;
      btn.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    });
  };

  // Sync count badges everywhere on the page.
  const syncCount = () => {
    const count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      if (el.classList.contains('header__wishlist-count')) {
        el.hidden = count === 0;
      }
    });
  };

  // Hydrate the drawer from storage
  const hydrate = async () => {
    if (!drawer) return;
    const handles = read();
    const list = drawer.querySelector('[data-wishlist-list]');
    const empty = drawer.querySelector('[data-wishlist-empty]');
    const cta = drawer.querySelector('[data-wishlist-cta]');
    if (!list || !empty || !cta) return;

    if (handles.length === 0) {
      drawer.removeAttribute('data-has-items');
      list.innerHTML = '';
      list.hidden = true;
      cta.hidden = true;
      empty.hidden = false;
      return;
    }

    drawer.setAttribute('data-has-items', 'true');
    empty.hidden = true;
    list.hidden = false;
    cta.hidden = false;
    list.innerHTML = '';

    const tpl = document.getElementById('WishlistItemTemplate');
    if (!tpl) return;

    const products = await Promise.all(handles.map((h) => fetchProduct(h)));

    products.forEach((p, idx) => {
      if (!p) return;
      const handle = handles[idx];
      const node = tpl.content.firstElementChild.cloneNode(true);
      node.dataset.handle = handle;

      const variant = p.variants?.[0] || {};
      const url = `/products/${handle}`;
      const img = node.querySelector('[data-wishlist-image]');
      const links = node.querySelectorAll('[data-wishlist-link]');
      const titleA = node.querySelector('.wishlist-drawer__item-title a');
      const price = node.querySelector('[data-wishlist-price]');
      const compare = node.querySelector('[data-wishlist-compare]');

      if (img) {
        img.src = p.featured_image || '';
        img.alt = p.title || '';
      }
      links.forEach((a) => {
        a.href = url;
        if (a.getAttribute('aria-label') !== null) a.setAttribute('aria-label', p.title || '');
      });
      if (titleA) titleA.textContent = p.title || '';
      if (price) price.textContent = formatMoney(variant.price ?? p.price);
      if (compare && variant.compare_at_price && variant.compare_at_price > variant.price) {
        compare.textContent = formatMoney(variant.compare_at_price);
        compare.hidden = false;
      }

      // Qty stepper
      const qtyEl = node.querySelector('[data-wishlist-qty]');
      node.querySelector('[data-wishlist-qty-dec]')?.addEventListener('click', () => {
        const cur = Math.max(1, Number(qtyEl.textContent) - 1);
        qtyEl.textContent = String(cur);
      });
      node.querySelector('[data-wishlist-qty-inc]')?.addEventListener('click', () => {
        const cur = Number(qtyEl.textContent) + 1;
        qtyEl.textContent = String(cur);
      });

      // Add to cart. A product with options (shade, size) opens its page so
      // the shopper picks one, instead of adding the first variant.
      const hasOptions = p.variants?.length > 1;
      const addBtn = node.querySelector('[data-wishlist-add]');
      if (hasOptions && addBtn) {
        const choose = window.theme?.strings?.choose || {};
        addBtn.setAttribute('aria-label', choose[p.options?.[0]?.name] || choose.default || 'Odaberi opciju');
      }
      addBtn?.addEventListener('click', async () => {
        if (hasOptions) {
          window.location.href = url;
          return;
        }
        const qty = Number(qtyEl?.textContent || 1);
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variant.id, quantity: qty }),
          });
          if (!res.ok) throw new Error('Add failed');
          // Pop the cart drawer if it exists
          const cartDrawer = document.querySelector('[data-cart-drawer]');
          if (cartDrawer) {
            close();
            const cartTrigger = document.querySelector('[data-cart-trigger]');
            cartTrigger?.click();
          }
        } catch {
          window.location.href = '/cart';
        }
      });

      // Remove
      node.querySelector('[data-wishlist-remove]')?.addEventListener('click', () => {
        remove(handle);
        hydrate();
      });

      list.appendChild(node);
    });
  };

  // Wire up triggers + close handlers
  const init = () => {
    document.querySelectorAll('[data-wishlist-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });

    drawer?.querySelectorAll('[data-wishlist-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer?.getAttribute('aria-hidden') === 'false') close();
    });

    drawer?.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => {
      clear();
      hydrate();
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.handle;
      if (!handle) return;
      toggle(handle);
      syncToggles();
      syncCount();
    });

    document.addEventListener('wishlist:change', () => {
      syncCount();
      syncToggles();
    });

    syncToggles();
    syncCount();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose minimal API
  window.Wishlist = { read, add, remove, toggle, has, clear, open, close };
  window.__BO_FETCH_PRODUCT__ = fetchProduct;
})();
